import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

type Option = { id: number; name: string }
type ItemOption = { id: number; code: string; name: string }
type TaxOption = { id: number; name: string; percent: number }
type PurchaseOrderListItem = {
  id: number
  code: string
  reference: string
  warehouse: string
  orderDate: string
  deliveryDate: string
  total: number
}

/** One line serialized into `purchaseOrderLinesJson` (multipart), matching API line model. */
export type PurchaseOrderSaveLine = {
  id: number
  itemId: number
  warehouseId: number
  quantity: number
  quantityInTransit: number
  unitPrice: number
  taxableAmount: number
  /** Tax rate id (lookup) */
  tax: number
  taxAmount: number
  taxPercent: number
  discount: number
  discountAmount: number
  lineAmount: number
  isDeleted: string
}

export type PurchaseOrderSavePayload = {
  id: number
  code: string
  vendorId: number
  vendorName: string
  /** Header date (often same as orderDate) */
  date: string
  branchId: number
  warehouseId: number
  warehouseName: string
  reference: string
  orderDate: string
  deliveryDate: string
  deliveryAddress: string
  paymentTerms: number
  termsAndConditions: string
  attachmentPath: string
  taxableAmount: number
  taxAmount: number
  discount: number
  total: number
  status: string
  createdDate: string
  createdBy: number
  modifiedDate: string
  modifiedBy: number
  attachmentFile?: File | null
  /** Serialized to `purchaseOrderLinesJson` on the wire. */
  itemList: PurchaseOrderSaveLine[]
}

type PurchaseOrderLinePayload = {
  id: number
  itemId: number
  warehouseId: number
  quantity: number
  quantityInTransit: number
  unitPrice: number
  taxableAmount: number
  tax: number
  taxAmount: number
  taxPercent: number
  discount: number
  discountAmount: number
  lineAmount: number
  isDeleted: string
}

export type PurchaseOrderMaster = {
  id: number
  code: string
  vendorId: number
  warehouseId: number
  reference: string
  orderDate: string
  deliveryDate: string
  deliveryAddress: string
  paymentTerms: number
  vendorName?: string
  warehouseName?: string
  taxableAmount?: number
  taxAmount?: number
  discount?: number
  total?: number
  status?: string
  attachmentPath?: string
  createdDate?: string
  createdBy?: number
  modifiedDate?: string
  modifiedBy?: number
}

const toArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.result)) return payload.result
  return []
}

const unwrap = (payload: any): any => {
  if (payload && typeof payload === 'object') {
    if ('data' in payload) return payload.data
  }
  return payload
}

export class PurchaseOrderService {
  async getAll(branchId: number, pageSize: number, pageNumber: number): Promise<{ items: PurchaseOrderListItem[]; totalCount: number }> {
    const response = await apiClient.get(endpoints.purchaseOrders.getAll(branchId, pageSize, pageNumber))
    const payload = response.data
    const raw = unwrap(payload)
    const arr = toArray(raw)
    const items = arr.map((x: any) => ({
      id: Number(x?.id ?? 0),
      code: String(x?.code ?? ''),
      reference: String(x?.reference ?? ''),
      warehouse: String(x?.warehouseName ?? x?.warehouse ?? x?.warehouseCode ?? ''),
      orderDate: String(x?.orderDate ?? x?.date ?? ''),
      deliveryDate: String(x?.deliveryDate ?? ''),
      total: Number(x?.total ?? 0),
    }))
    const totalCount = Number(raw?.totalRecords ?? raw?.totalCount ?? payload?.totalRecords ?? payload?.totalCount ?? items.length)
    return { items, totalCount: Number.isFinite(totalCount) ? totalCount : items.length }
  }

  async getItems(): Promise<ItemOption[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getItemList)
    const arr = toArray(unwrap(response.data))
    return arr.map((x: any) => ({
      id: Number(x?.itemId ?? x?.id ?? 0),
      code: String(x?.itemCode ?? ''),
      name: String(x?.itemNameEn ?? x?.itemNameAr ?? x?.itemName ?? ''),
    }))
  }

  async getById(id: number): Promise<PurchaseOrderMaster> {
    const response = await apiClient.get(endpoints.purchaseOrders.getById(id))
    const payload = unwrap(response.data)
    const row = Array.isArray(payload) ? payload[0] : payload
    return {
      id: Number(row?.id ?? 0),
      code: String(row?.code ?? ''),
      vendorId: Number(row?.vendorId ?? 0),
      warehouseId: Number(row?.warehouseId ?? 0),
      reference: String(row?.reference ?? ''),
      orderDate: String(row?.orderDate ?? row?.date ?? ''),
      deliveryDate: String(row?.deliveryDate ?? ''),
      deliveryAddress: String(row?.deliveryAddress ?? ''),
      paymentTerms: Number(row?.paymentTerms ?? 0),
      vendorName: row?.vendorName != null ? String(row.vendorName) : undefined,
      warehouseName: row?.warehouseName != null ? String(row.warehouseName) : undefined,
      taxableAmount: row?.taxableAmount != null ? Number(row.taxableAmount) : undefined,
      taxAmount: row?.taxAmount != null ? Number(row.taxAmount) : undefined,
      discount: row?.discount != null ? Number(row.discount) : undefined,
      total: row?.total != null ? Number(row.total) : undefined,
      status: row?.status != null ? String(row.status) : undefined,
      attachmentPath: row?.attachmentPath != null ? String(row.attachmentPath) : undefined,
      createdDate: row?.createdDate != null ? String(row.createdDate) : undefined,
      createdBy: row?.createdBy != null ? Number(row.createdBy) : undefined,
      modifiedDate: row?.modifiedDate != null ? String(row.modifiedDate) : undefined,
      modifiedBy: row?.modifiedBy != null ? Number(row.modifiedBy) : undefined,
    }
  }

  async getLinesByOrderId(orderId: number): Promise<PurchaseOrderLinePayload[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getByOrderId(orderId))
    const payload = unwrap(response.data)
    const arr = toArray(payload)
    return arr.map((x: any) => ({
      id: Number(x?.id ?? 0),
      itemId: Number(x?.itemId ?? 0),
      warehouseId: Number(x?.warehouseId ?? 0),
      quantity: Number(x?.quantity ?? 0),
      quantityInTransit: Number(x?.quantityInTransit ?? 0),
      unitPrice: Number(x?.unitPrice ?? 0),
      taxableAmount: Number(x?.taxableAmount ?? 0),
      tax: Number(x?.tax ?? 0),
      taxAmount: Number(x?.taxAmount ?? 0),
      taxPercent: Number(x?.taxPercent ?? 0),
      discount: Number(x?.discount ?? 0),
      discountAmount: Number(x?.discountAmount ?? 0),
      lineAmount: Number(x?.lineAmount ?? 0),
      isDeleted: String(x?.isDeleted ?? 'false'),
    }))
  }

  async getTaxRates(): Promise<TaxOption[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getAllTaxRates)
    const arr = toArray(unwrap(response.data))
    return arr.map((x: any) => ({
      id: Number(x?.taxRateId ?? x?.id ?? 0),
      name: String(x?.taxRateNameEn ?? x?.taxRateNameAr ?? x?.lookupNameEn ?? x?.lookupNameAr ?? 'Tax'),
      percent: Number(x?.taxPercent ?? x?.taxRate ?? 0),
    }))
  }

  async getPaymentTerms(): Promise<Option[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getPaymentTerms)
    const arr = toArray(unwrap(response.data))
    return arr.map((x: any) => ({
      id: Number(x?.id ?? x?.lookupId ?? 0),
      name: String(x?.lookupNameEn ?? x?.lookupNameAr ?? x?.paymentTermNameEn ?? x?.paymentTermNameAr ?? ''),
    }))
  }

  async getVendors(branchId: number): Promise<Option[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getAllVendors(branchId))
    const arr = toArray(unwrap(response.data))
    return arr.map((x: any) => ({
      id: Number(x?.vendorId ?? x?.id ?? 0),
      name: String(x?.vendorNameEn ?? x?.vendorNameAr ?? x?.vendorName ?? ''),
    }))
  }

  async getWarehouses(branchId: number): Promise<Option[]> {
    const response = await apiClient.get(endpoints.purchaseOrders.getAllWarehouses(branchId))
    const arr = toArray(unwrap(response.data))
    return arr.map((x: any) => ({
      id: Number(x?.id ?? x?.warehouseId ?? 0),
      name: String(x?.name ?? x?.warehouseName ?? x?.code ?? ''),
    }))
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  /** Same property order as Swagger line template (stable JSON for the API). */
  private normalizeLineForJson(l: PurchaseOrderSaveLine): Record<string, number | string> {
    return {
      discount: l.discount,
      quantityInTransit: l.quantityInTransit,
      taxAmount: l.taxAmount,
      unitPrice: l.unitPrice,
      quantity: l.quantity,
      tax: l.tax,
      lineAmount: l.lineAmount,
      itemId: l.itemId,
      isDeleted: l.isDeleted,
      discountAmount: l.discountAmount,
      taxPercent: l.taxPercent,
      id: l.id,
      taxableAmount: l.taxableAmount,
      warehouseId: l.warehouseId,
    }
  }

  private buildPurchaseOrderLinesJson(lines: PurchaseOrderSaveLine[]): string {
    return JSON.stringify(lines.map((l) => this.normalizeLineForJson(l)))
  }

  private logOutboundPayload(action: 'save' | 'update', payload: PurchaseOrderSavePayload, purchaseOrderLinesJson: string) {
    const { attachmentFile, itemList, ...master } = payload
    console.log(`[PurchaseOrders] ${action} — master (multipart text fields)`, master)
    console.log(`[PurchaseOrders] ${action} — purchaseOrderLinesJson (form field value)`, purchaseOrderLinesJson)
    console.log(`[PurchaseOrders] ${action} — lines (parsed)`, itemList)
    console.log(
      `[PurchaseOrders] ${action} — AttachmentFile`,
      attachmentFile ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type } : null,
    )
  }

  private buildSaveUpdateFormData(payload: PurchaseOrderSavePayload, purchaseOrderLinesJson: string): FormData {
    const formData = new FormData()
    const resolvedCode =
      String(payload.code ?? '').trim() ||
      String(payload.reference ?? '').trim() ||
      String(payload.vendorName ?? '').trim() ||
      `PO-${Date.now()}`

    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'Code', resolvedCode)
    this.appendFormField(formData, 'VendorId', payload.vendorId)
    this.appendFormField(formData, 'VendorName', payload.vendorName)
    this.appendFormField(formData, 'Date', payload.date)
    this.appendFormField(formData, 'BranchId', payload.branchId)
    this.appendFormField(formData, 'WarehouseId', payload.warehouseId)
    this.appendFormField(formData, 'WarehouseName', payload.warehouseName)
    this.appendFormField(formData, 'Reference', payload.reference)
    this.appendFormField(formData, 'OrderDate', payload.orderDate)
    this.appendFormField(formData, 'DeliveryDate', payload.deliveryDate)
    this.appendFormField(formData, 'DeliveryAddress', payload.deliveryAddress)
    this.appendFormField(formData, 'PaymentTerms', payload.paymentTerms)
    this.appendFormField(formData, 'TermsAndConditions', payload.termsAndConditions)
    this.appendFormField(formData, 'AttachmentPath', payload.attachmentPath)
    this.appendFormField(formData, 'TaxableAmount', payload.taxableAmount)
    this.appendFormField(formData, 'TaxAmount', payload.taxAmount)
    this.appendFormField(formData, 'Discount', payload.discount)
    this.appendFormField(formData, 'Total', payload.total)
    this.appendFormField(formData, 'Status', payload.status)
    this.appendFormField(formData, 'CreatedDate', payload.createdDate)
    this.appendFormField(formData, 'CreatedBy', payload.createdBy)
    this.appendFormField(formData, 'ModifiedDate', payload.modifiedDate)
    this.appendFormField(formData, 'ModifiedBy', payload.modifiedBy)

    /** Swagger shows `purchaseOrderLinesJson`; align with backend [FromForm] name. */
    this.appendFormField(formData, 'purchaseOrderLinesJson', purchaseOrderLinesJson)

    if (payload.attachmentFile) {
      formData.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    return formData
  }

  async save(payload: PurchaseOrderSavePayload): Promise<void> {
    const purchaseOrderLinesJson = this.buildPurchaseOrderLinesJson(payload.itemList)
    this.logOutboundPayload('save', payload, purchaseOrderLinesJson)
    const formData = this.buildSaveUpdateFormData(payload, purchaseOrderLinesJson)
    await apiClient.post(endpoints.purchaseOrders.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async update(payload: PurchaseOrderSavePayload): Promise<void> {
    const purchaseOrderLinesJson = this.buildPurchaseOrderLinesJson(payload.itemList)
    this.logOutboundPayload('update', payload, purchaseOrderLinesJson)
    const formData = this.buildSaveUpdateFormData(payload, purchaseOrderLinesJson)
    await apiClient.post(endpoints.purchaseOrders.update, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
}

export const purchaseOrderService = new PurchaseOrderService()
